import React, { useState } from 'react';
import { Box, Divider, Modal, MenuItem, Stack, Typography } from '@mui/material';
import ChevronDownIcon from '@mui/icons-material/ExpandMore';
import SettingsIcon from '@mui/icons-material/Settings';
import PersonIcon from '@mui/icons-material/Person';
import SecurityIcon from '@mui/icons-material/Security';
import NotificationsIcon from '@mui/icons-material/Notifications';
import PaymentIcon from '@mui/icons-material/Payment';
import { theme } from '../../constants/theme';
import { useThemeContext } from '../../hook/useThemeManager';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { useLocation } from 'react-router-dom';

interface MenuItemType {
    name: string;
    route: string;
}

interface AccountSettingsMenuProps {
    menuItems: MenuItemType[];
    onItemClick: (menuItem: MenuItemType) => void;
}

// Icon mapping for menu items
const getIconForMenuItem = (itemName: string) => {
    const name = itemName.toLowerCase();
    if (name.includes('profile') || name.includes('account')) return <PersonIcon />;
    if (name.includes('security') || name.includes('password')) return <SecurityIcon />;
    if (name.includes('notification')) return <NotificationsIcon />;
    if (name.includes('payment') || name.includes('billing')) return <PaymentIcon />;
    return <SettingsIcon />; // Default icon
};

export const AccountSettingsMenu: React.FC<AccountSettingsMenuProps> = ({ menuItems, onItemClick }) => {
    const [selectedItem, setSelectedItem] = useState<string>(menuItems[0].name);
    const [openModal, setOpenModal] = useState<boolean>(false);
    const { device } = useThemeContext();
    const { themeStyle } = useThemeContext();
    const location = useLocation();

    const handleMenuItemClick = (item: MenuItemType) => {
        setSelectedItem(item.name);
        setOpenModal(false);
        onItemClick && onItemClick(item);
    };

    const handleMobileOpen = () => setOpenModal(true);
    const handleMobileClose = () => setOpenModal(false);

    return (
        <Box sx={{ width: '100%' }}>
            {!device.isDesktop ? (
                // Mobile: Dropdown style
                <Box>
                    <Stack
                        direction="row"
                        width={'100%'}
                        justifyContent="space-between"
                        p={2}
                        border={`0.5px solid ${theme[themeStyle].tag_white}`}
                        alignItems="center"
                        mb={2}
                        borderRadius={2}
                        onClick={handleMobileOpen}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            {getIconForMenuItem(selectedItem)}
                            <Typography
                                style={{
                                    color: theme[themeStyle].tag_white,
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    fontSize: 14,
                                    textTransform: 'capitalize',
                                }}>
                                {selectedItem}
                            </Typography>
                        </Stack>
                        <ChevronDownIcon sx={{ color: theme[themeStyle].tag_white }} />
                    </Stack>

                    {/* Modal for Mobile Menu */}
                    <Modal open={openModal} onClose={handleMobileClose}>
                        <Box
                            sx={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                width: '90%',
                                bgcolor: theme[themeStyle].secondaryBg,
                                borderRadius: 2,
                                boxShadow: 24,
                                p: 2,
                            }}>
                            {menuItems.map((item, index) => {
                                return (
                                    <Box key={index}>
                                        <MenuItem
                                            onClick={() => handleMenuItemClick(item)}
                                            selected={location.pathname.endsWith(item.route)}
                                            sx={{
                                                justifyContent: 'flex-start',
                                                color: theme[themeStyle].tag_white,
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                fontSize: 14,
                                                textTransform: 'capitalize',
                                                gap: 1,
                                                '&:hover': {
                                                    backgroundColor: theme[themeStyle].deep_grey,
                                                    color: theme[themeStyle].tag_white,
                                                },
                                                '&.Mui-selected': {
                                                    backgroundColor: theme[themeStyle].deep_grey,
                                                    color: theme[themeStyle].tag_white,
                                                },
                                                '&.Mui-selected:hover': {
                                                    backgroundColor: theme[themeStyle].deep_grey,
                                                },
                                            }}>
                                            {getIconForMenuItem(item.name)}
                                            {item.name}
                                        </MenuItem>
                                        {index < menuItems.length - 1 && (
                                            <Divider sx={{ backgroundColor: theme[themeStyle].darkGray60 }} />
                                        )}
                                    </Box>
                                );
                            })}
                        </Box>
                    </Modal>
                </Box>
            ) : (
                // Desktop: Horizontal layout with all icons visible
                <Box
                    sx={{
                        display: 'flex',
                        border: `1px solid ${theme[themeStyle].darkGray60}`,
                        borderRadius: 2,
                        backgroundColor: theme[themeStyle].deep_grey_lighter,
                        overflow: 'hidden',
                        width: '100%',
                        justifyContent: 'flex-start',
                    }}>
                    {menuItems.map((item, index) => (
                        <Box key={index} sx={{ minWidth: '120px', flex: '0 0 auto' }}>
                            <Box
                                onClick={() => handleMenuItemClick(item)}
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    p: 2,
                                    cursor: 'pointer',
                                    color: location.pathname.endsWith(item.route)
                                        ? theme[themeStyle].tag_white
                                        : theme[themeStyle].tag_white,
                                    backgroundColor: location.pathname.endsWith(item.route)
                                        ? theme[themeStyle].deep_grey
                                        : 'transparent',
                                    borderBottom: location.pathname.endsWith(item.route)
                                        ? `2px solid ${theme[themeStyle].tag_white}`
                                        : '2px solid transparent',
                                    '&:hover': {
                                        backgroundColor: theme[themeStyle].deep_grey,
                                    },
                                    transition: 'all 0.2s ease',
                                }}>
                                <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    {getIconForMenuItem(item.name)}
                                </Box>
                                <Typography
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        fontSize: 12,
                                        textTransform: 'capitalize',
                                        textAlign: 'center',
                                    }}>
                                    {item.name}
                                </Typography>
                            </Box>
                            {index < menuItems.length - 1 && (
                                <Divider
                                    orientation="vertical"
                                    flexItem
                                    sx={{ backgroundColor: theme[themeStyle].darkGray60 }}
                                />
                            )}
                        </Box>
                    ))}
                </Box>
            )}
        </Box>
    );
};
